import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { DailyProductionReport } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import MesDateSelector, {
  getDefaultMesDate,
  saveMesDate,
} from "../../../components/MES/MesDateSelector";
import { buildMachineTimelineSvg } from "./machineTimelineSvg";
import { buildDailyReportDocument } from "./dailyReportTemplate";
import {
  diffClass,
  fmtClock,
  fmtDateHr,
  fmtDayClock,
  fmtDec,
  fmtEur,
  fmtInt,
  fmtKg,
  fmtPct,
  fmtSignedEur,
  fmtSignedInt,
  fmtSignedPct,
} from "./reportFormat";

type ReportTab = "summary" | "plan" | "actual";

const TABS: { key: ReportTab; label: string }[] = [
  { key: "summary", label: "Sažetak" },
  { key: "plan", label: "Plan" },
  { key: "actual", label: "Ostvareno" },
];

/**
 * Dnevni proizvodno-financijski izvještaj (plan vs. ostvareno).
 *
 * Sve brojke dolaze gotove s backenda (`/api/mes/reports/daily-production`)
 * kako bi ekran i ispisani PDF prikazali istu vrijednost — ovdje se samo
 * formatira i raspoređuje.
 */
const DailyReport = () => {
  savePathToLocalStorage();

  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate());
  const [report, setReport] = useState<DailyProductionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<ReportTab>("summary");

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await mesApi.getDailyProductionReport(selectedDate);
      setReport(data);
    } catch (err: any) {
      setReport(null);
      toast.error("Greška pri učitavanju izvještaja: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const chartMarkup = useMemo(
    () =>
      report ? buildMachineTimelineSvg(report.machine_timeline, { theme: "dark", width: 1000 }) : "",
    [report]
  );

  const handlePrint = () => {
    if (!report) return;

    const printWindow = window.open("", "_blank", "width=1000,height=1200");
    if (!printWindow) {
      toast.error("Otvaranje ispisa blokirano. Dozvolite skočne prozore (pop-ups).");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(buildDailyReportDocument(report));
    printWindow.document.close();
  };

  const renderHeaderMeta = (data: DailyProductionReport) => {
    const timeline = data.final_timeline;
    return (
      <div className="mes-section report-meta">
        <div className="report-meta-main">
          <h3>Dnevni proizvodni izvještaj — {fmtDateHr(data.target_date)}</h3>
          <div className="report-meta-facts">
            <span>
              Konačni plan: <b>{timeline ? `TL-${timeline.id} (v${timeline.version})` : "N/A"}</b>
            </span>
            <span>
              Potvrđen:{" "}
              <b>{timeline?.confirmed_at ? fmtDayClock(timeline.confirmed_at) : "Nepotvrđen"}</b>
            </span>
            <span>
              Start rada: <b>{fmtClock(data.prod_start)}</b>
            </span>
            <span>
              Verzija plana u bazi: <b>{data.timeline_versions_cnt}</b>
            </span>
            <span>
              Promjene naloga: <b>{data.changes_before_start}</b> prije starta /{" "}
              <b>{data.changes_during_prod}</b> real-time
            </span>
          </div>
        </div>
        <div className="report-meta-actions no-print">
          <button className="btn-refresh" onClick={loadReport} disabled={loading}>
            {loading ? "Učitavanje…" : "Osvježi"}
          </button>
          <button className="btn-print" onClick={handlePrint} disabled={loading}>
            Ispis / PDF
          </button>
        </div>
      </div>
    );
  };

  const renderKpiTable = (data: DailyProductionReport) => {
    const plan = data.plan_totals;
    const real = data.real_totals;

    const diffBatches = real.batches_cnt - plan.batches_cnt;
    const diffPieces = real.packed_pieces - plan.pieces;
    const diffVpc = real.vpc_value - plan.vpc_value;
    const diffCost = real.ingredient_cost - plan.ingredient_cost;
    const diffYield = real.yield_pct - 100;

    const batchPct = plan.batches_cnt > 0 ? (real.batches_cnt / plan.batches_cnt) * 100 : 0;
    const piecesPct = plan.pieces > 0 ? (real.packed_pieces / plan.pieces) * 100 : 0;

    return (
      <div className="mes-section">
        <h3>Glavne proizvodne metrike (plan vs. ostvareno)</h3>
        <div className="mes-table-wrap is-tall">
          <table className="mes-table report-kpi-table">
            <thead>
              <tr>
                <th>Ključna metrika (KPI)</th>
                <th className="num">Planirano</th>
                <th className="num">Ostvareno</th>
                <th className="num">Odstupanje (razlika)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Broj zamjesa (šarži)</td>
                <td className="num">{fmtInt(plan.batches_cnt)}</td>
                <td className="num">{fmtInt(real.batches_cnt)}</td>
                <td className={`num ${diffClass(diffBatches)}`}>
                  {fmtSignedInt(diffBatches)} ({fmtDec(batchPct, 0)} %)
                </td>
              </tr>
              <tr>
                <td>Gotovi komadi (zapakirano)</td>
                <td className="num">{fmtInt(plan.pieces)} kom</td>
                <td className="num">{fmtInt(real.packed_pieces)} kom</td>
                <td className={`num ${diffClass(diffPieces)}`}>
                  {fmtSignedInt(diffPieces)} kom ({fmtDec(piecesPct, 1)} %)
                </td>
              </tr>
              <tr>
                <td>Evidentirani škart</td>
                <td className="num">0 kom (0,0 %)</td>
                <td className="num">
                  {fmtInt(real.scrap_pieces)} kom ({fmtDec(real.scrap_pct, 1)} %)
                </td>
                <td className={`num ${real.scrap_pieces > 0 ? "diff-neg" : "diff-flat"}`}>
                  +{fmtInt(real.scrap_pieces)} kom
                </td>
              </tr>
              <tr>
                <td>Yield (iskorištenje)</td>
                <td className="num">{fmtPct(100)}</td>
                <td className="num">{fmtPct(real.yield_pct)}</td>
                <td className={`num ${diffClass(diffYield)}`}>{fmtSignedPct(diffYield)}</td>
              </tr>
              <tr>
                <td>VPC vrijednost zapakirano</td>
                <td className="num">{fmtEur(plan.vpc_value)}</td>
                <td className="num">{fmtEur(real.vpc_value)}</td>
                <td className={`num ${diffClass(diffVpc)}`}>{fmtSignedEur(diffVpc)}</td>
              </tr>
              <tr>
                <td>Trošak primarnih sirovina</td>
                <td className="num">{fmtEur(plan.ingredient_cost)}</td>
                <td className="num">{fmtEur(real.ingredient_cost)}</td>
                <td className={`num ${diffClass(diffCost, false)}`}>{fmtSignedEur(diffCost)}</td>
              </tr>
              <tr className="is-pending">
                <td>Trošak pakiranja</td>
                <td className="num">—</td>
                <td className="num">—</td>
                <td className="num">— (priprema u bazi)</td>
              </tr>
              <tr className="is-pending">
                <td>Trošak rada (osoblje)</td>
                <td className="num">—</td>
                <td className="num">—</td>
                <td className="num">— (priprema u bazi)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderBanner = (data: DailyProductionReport) => {
    const plan = data.plan_totals;
    const real = data.real_totals;
    const diffMargin = real.margin - plan.margin;
    const diffMarginPct = real.margin_pct - plan.margin_pct;

    return (
      <div className="report-banner-grid">
        <div className="mes-metric-card report-banner-card">
          <div className="metric-label">Bruto marža</div>
          <div className="metric-value">{fmtEur(real.margin)}</div>
          <div className="metric-delta">Plan: {fmtEur(plan.margin)}</div>
          <div className={`metric-delta ${diffClass(diffMargin)}`}>
            Razlika: {fmtSignedEur(diffMargin)}
          </div>
        </div>
        <div className="mes-metric-card report-banner-card">
          <div className="metric-label">Udio škarta</div>
          <div className={`metric-value ${real.scrap_pct > 0 ? "diff-neg" : "diff-pos"}`}>
            {fmtPct(real.scrap_pct)}
          </div>
          <div className="metric-delta">Plan: 0,0 %</div>
          <div className={`metric-delta ${real.scrap_pct > 0 ? "diff-neg" : "diff-pos"}`}>
            Razlika: +{fmtDec(real.scrap_pct, 1)} %
          </div>
        </div>
        <div className="mes-metric-card report-banner-card">
          <div className="metric-label">Marža / VPC zapakirano</div>
          <div className="metric-value">{fmtPct(real.margin_pct)}</div>
          <div className="metric-delta">Plan: {fmtPct(plan.margin_pct)}</div>
          <div className={`metric-delta ${diffClass(diffMarginPct)}`}>
            Razlika: {fmtSignedPct(diffMarginPct)}
          </div>
        </div>
        <div className="mes-metric-card report-banner-card is-pending">
          <div className="metric-label">Rad / VPC proizvedeno</div>
          <div className="metric-value">—</div>
          <div className="metric-delta">Plan: —</div>
          <div className="metric-delta">Razlika: —</div>
        </div>
      </div>
    );
  };

  const renderTimeline = () => (
    <div className="mes-section">
      <h3>Strojevi — radni timeline (planirano vs. ostvareno)</h3>
      {chartMarkup ? (
        <div className="report-chart" dangerouslySetInnerHTML={{ __html: chartMarkup }} />
      ) : (
        <p className="report-empty">
          Nema unesenih satnica za prikaz timelinea na strojevima za ovaj datum.
        </p>
      )}
    </div>
  );

  const renderComparison = (data: DailyProductionReport) => {
    const plan = data.plan_totals;
    const real = data.real_totals;
    const diffVpc = real.vpc_value - plan.vpc_value;

    return (
      <div className="mes-section">
        <h3>Detaljno sučeljavanje po proizvodu (plan vs. ostvareno)</h3>
        {data.comparison.length === 0 ? (
          <p className="report-empty">Nema podataka za sučeljavanje po proizvodu.</p>
        ) : (
          <div className="mes-table-wrap is-tall">
            <table className="mes-table mes-table-compact">
              <thead>
                <tr>
                  <th>Proizvod</th>
                  <th className="num">Šarži (Pl/Os)</th>
                  <th className="num">Komada (Pl/Os)</th>
                  <th className="num">Odstupanje kom</th>
                  <th className="num">Škart kom (%)</th>
                  <th className="num">Yield (%)</th>
                  <th className="num">Plan VPC</th>
                  <th className="num">VPC zapakirano</th>
                  <th className="num">Razlika VPC</th>
                  <th className="num">Razlika troška</th>
                </tr>
              </thead>
              <tbody>
                {data.comparison.map((row) => (
                  <tr key={row.product_name}>
                    <td>{row.product_name}</td>
                    <td className="num">
                      {fmtInt(row.plan_batches)} / {fmtInt(row.real_batches)}
                    </td>
                    <td className="num">
                      {fmtInt(row.plan_pieces)} / {fmtInt(row.real_pieces)}
                    </td>
                    <td className={`num ${diffClass(row.diff_pieces)}`}>
                      {row.plan_pieces > 0 ? fmtSignedInt(row.diff_pieces) : "-"}
                    </td>
                    <td className="num">
                      {row.scrap_pct == null
                        ? "-"
                        : `${fmtInt(row.scrap_pieces)} (${fmtDec(row.scrap_pct, 1)} %)`}
                    </td>
                    <td className="num">{row.yield_pct == null ? "-" : fmtPct(row.yield_pct)}</td>
                    <td className="num">{fmtEur(row.plan_vpc)}</td>
                    <td className="num">{fmtEur(row.real_vpc)}</td>
                    <td className={`num ${diffClass(row.diff_vpc)}`}>
                      {fmtSignedEur(row.diff_vpc)}
                    </td>
                    <td className={`num ${diffClass(row.diff_cost, false)}`}>
                      {fmtSignedEur(row.diff_cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>UKUPNO ZA DAN</td>
                  <td className="num">
                    {fmtInt(plan.batches_cnt)} / {fmtInt(real.batches_cnt)}
                  </td>
                  <td className="num">
                    {fmtInt(plan.pieces)} / {fmtInt(real.packed_pieces)}
                  </td>
                  <td className={`num ${diffClass(real.packed_pieces - plan.pieces)}`}>
                    {fmtSignedInt(real.packed_pieces - plan.pieces)}
                  </td>
                  <td className="num">
                    {fmtInt(real.scrap_pieces)} ({fmtDec(real.scrap_pct, 1)} %)
                  </td>
                  <td className="num">{fmtPct(real.yield_pct)}</td>
                  <td className="num">{fmtEur(plan.vpc_value)}</td>
                  <td className="num">{fmtEur(real.vpc_value)}</td>
                  <td className={`num ${diffClass(diffVpc)}`}>{fmtSignedEur(diffVpc)}</td>
                  <td
                    className={`num ${diffClass(real.ingredient_cost - plan.ingredient_cost, false)}`}
                  >
                    {fmtSignedEur(real.ingredient_cost - plan.ingredient_cost)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderOperationalPlan = (data: DailyProductionReport) => (
    <div className="mes-section">
      <h3>
        Operativni plan rasporeda na strojevima
        {data.final_timeline ? ` (TL-${data.final_timeline.id})` : ""}
      </h3>
      {data.planned_batches.length === 0 ? (
        <p className="report-empty">Nema planiranih zamjesa za ovaj datum.</p>
      ) : (
        <div className="mes-table-wrap">
          <table className="mes-table mes-table-compact">
            <thead>
              <tr>
                <th className="num">#</th>
                <th>Šarža / MixCode</th>
                <th>Proizvod</th>
                <th className="num">Plan masa</th>
                <th>Mikser</th>
                <th className="num">Zamjes</th>
                <th>Peć</th>
                <th className="num">Pečenje</th>
                <th className="num">Plan komada</th>
                <th className="num">Trošak sirovina</th>
              </tr>
            </thead>
            <tbody>
              {data.planned_batches.map((batch) => (
                <tr key={batch.batch_id}>
                  <td className="num">{batch.sequence}</td>
                  <td className="nowrap">
                    <code>{batch.mix_code}</code>
                  </td>
                  <td>{batch.product_name}</td>
                  <td className="num">{fmtKg(batch.final_mass_kg)}</td>
                  <td>{batch.mixer}</td>
                  <td className="num nowrap">
                    {fmtClock(batch.mixing_start)} - {fmtClock(batch.mixing_end)}
                  </td>
                  <td>{batch.oven}</td>
                  <td className="num nowrap">
                    {fmtClock(batch.baking_start)} - {fmtClock(batch.baking_end)}
                  </td>
                  <td className="num">
                    {batch.planned_pieces > 0 ? fmtInt(batch.planned_pieces) : "-"}
                  </td>
                  <td className="num">{fmtEur(batch.total_ingredient_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderFinancialPlan = (data: DailyProductionReport) => {
    const totals = data.plan_totals;
    const finished = data.plan_grouped.filter((group) => !group.is_semi_finished);
    const semiFinished = data.plan_grouped.filter((group) => group.is_semi_finished);

    return (
      <div className="mes-section">
        <h3>Financijski plan (grupirano po proizvodu)</h3>
        {data.plan_grouped.length === 0 ? (
          <p className="report-empty">Nema planiranih zamjesa za ovaj datum.</p>
        ) : (
          <div className="mes-table-wrap">
            <table className="mes-table mes-table-compact">
              <thead>
                <tr>
                  <th>Proizvod</th>
                  <th className="num">Šarži</th>
                  <th className="num">Masa</th>
                  <th className="num">Komada</th>
                  <th className="num">Trošak sirovina</th>
                  <th className="num">Sirovine/kom</th>
                  <th className="num">VPC/kom</th>
                  <th className="num">VPC ukupno</th>
                  <th className="num">Marža</th>
                </tr>
              </thead>
              <tbody>
                {finished.map((group) => (
                  <tr key={group.product_name}>
                    <td>{group.product_name}</td>
                    <td className="num">{fmtInt(group.batches_cnt)}</td>
                    <td className="num">{fmtKg(group.total_mass_kg)}</td>
                    <td className="num">
                      {group.total_pieces > 0 ? fmtInt(group.total_pieces) : "-"}
                    </td>
                    <td className="num">{fmtEur(group.total_ingredient_cost)}</td>
                    <td className="num">
                      {group.cost_per_piece > 0 ? fmtEur(group.cost_per_piece) : "-"}
                    </td>
                    <td className="num">
                      {group.vpc_unit_price > 0 ? fmtEur(group.vpc_unit_price) : "-"}
                    </td>
                    <td className="num">{fmtEur(group.total_vpc_value)}</td>
                    <td className={`num ${diffClass(group.margin_eur)}`}>
                      {fmtEur(group.margin_eur)}
                    </td>
                  </tr>
                ))}
                {semiFinished.map((group) => (
                  <tr key={group.product_name} className="report-row-sf">
                    <td>[poluproizvod] {group.product_name}</td>
                    <td className="num">{fmtInt(group.batches_cnt)}</td>
                    <td className="num">{fmtKg(group.total_mass_kg)}</td>
                    <td className="num">-</td>
                    <td className="num">{fmtEur(group.total_ingredient_cost)}</td>
                    <td className="num">-</td>
                    <td className="num">-</td>
                    <td className="num">-</td>
                    <td className="num">-</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>PLANIRANO UKUPNO ZA DAN</td>
                  <td className="num">{fmtInt(totals.batches_cnt)}</td>
                  <td className="num">{fmtKg(totals.mass_kg)}</td>
                  <td className="num">{fmtInt(totals.pieces)}</td>
                  <td className="num">{fmtEur(totals.ingredient_cost)}</td>
                  <td className="num" />
                  <td className="num" />
                  <td className="num">{fmtEur(totals.vpc_value)}</td>
                  <td className="num">
                    {fmtEur(totals.margin)} ({fmtDec(totals.margin_pct, 1)} %)
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {semiFinished.length > 0 && (
          <p className="report-footnote">
            Poluproizvodi su informativni — njihov trošak je već uračunat u gotove proizvode kroz
            podrecepture, pa ne ulazi u dnevni zbroj.
          </p>
        )}
      </div>
    );
  };

  const renderRealized = (data: DailyProductionReport) => {
    const totals = data.real_totals;

    return (
      <>
        <div className="mes-section">
          <h3>Financijsko ostvareno (grupirano po proizvodu)</h3>
          {data.realized_grouped.length === 0 ? (
            <p className="report-empty">
              Nema evidentiranih ostvarenih šarži za ovaj datum. Upišite procesne podatke u sekciji
              Izvršavanje.
            </p>
          ) : (
            <div className="mes-table-wrap">
              <table className="mes-table mes-table-compact">
                <thead>
                  <tr>
                    <th>Proizvod</th>
                    <th className="num">Šarži</th>
                    <th className="num">Masa</th>
                    <th className="num">Proizvedeno</th>
                    <th className="num">Zapakirano</th>
                    <th className="num">Škart (kom / %)</th>
                    <th className="num">Stvarni trošak</th>
                    <th className="num">Ostvareni VPC</th>
                    <th className="num">Marža</th>
                  </tr>
                </thead>
                <tbody>
                  {data.realized_grouped.map((group) => (
                    <tr key={group.product_name}>
                      <td>{group.product_name}</td>
                      <td className="num">{fmtInt(group.batches_cnt)}</td>
                      <td className="num">{fmtKg(group.total_mass_kg)}</td>
                      <td className="num">{fmtInt(group.produced_pieces)}</td>
                      <td className="num">{fmtInt(group.packed_pieces)}</td>
                      <td className="num">
                        {fmtInt(group.scrap_pieces)} ({fmtDec(group.scrap_pct, 1)} %)
                      </td>
                      <td className="num">{fmtEur(group.actual_ingredient_cost)}</td>
                      <td className="num">{fmtEur(group.total_vpc_value)}</td>
                      <td className={`num ${diffClass(group.margin_eur)}`}>
                        {fmtEur(group.margin_eur)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>OSTVARENO UKUPNO ZA DAN</td>
                    <td className="num">{fmtInt(totals.batches_cnt)}</td>
                    <td className="num">{fmtKg(totals.mass_kg)}</td>
                    <td className="num">{fmtInt(totals.produced_pieces)}</td>
                    <td className="num">{fmtInt(totals.packed_pieces)}</td>
                    <td className="num">
                      {fmtInt(totals.scrap_pieces)} ({fmtDec(totals.scrap_pct, 1)} %)
                    </td>
                    <td className="num">{fmtEur(totals.ingredient_cost)}</td>
                    <td className="num">{fmtEur(totals.vpc_value)}</td>
                    <td className="num">
                      {fmtEur(totals.margin)} ({fmtDec(totals.margin_pct, 1)} %)
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {data.realized_batches.length > 0 && (
          <div className="mes-section">
            <h3>Ostvarene šarže</h3>
            <div className="mes-table-wrap">
              <table className="mes-table mes-table-compact">
                <thead>
                  <tr>
                    <th>Šarža / MixCode</th>
                    <th>Proizvod</th>
                    <th className="num">Masa</th>
                    <th className="num">Proizvedeno</th>
                    <th className="num">Zapakirano</th>
                    <th className="num">Škart</th>
                    <th>Mikser</th>
                    <th className="num">Zamjes</th>
                    <th>Peć</th>
                    <th className="num">Pečenje</th>
                    <th className="num">Ostvareni VPC</th>
                  </tr>
                </thead>
                <tbody>
                  {data.realized_batches.map((batch) => (
                    <tr key={batch.id}>
                      <td className="nowrap">
                        <code>{batch.mix_code}</code>
                      </td>
                      <td>{batch.product_name}</td>
                      <td className="num">{fmtKg(batch.dough_mass_kg)}</td>
                      <td className="num">{fmtInt(batch.produced_pieces)}</td>
                      <td className="num">{fmtInt(batch.packed_pieces)}</td>
                      <td className="num">{fmtInt(batch.scrap_pieces)}</td>
                      <td>{batch.mixer}</td>
                      <td className="num nowrap">
                        {fmtClock(batch.mixing_start)} - {fmtClock(batch.mixing_end)}
                      </td>
                      <td>{batch.oven}</td>
                      <td className="num nowrap">
                        {fmtClock(batch.baking_start)} - {fmtClock(batch.baking_end)}
                      </td>
                      <td className="num">{fmtEur(batch.realized_vpc_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="mes-page daily-report-page">
      <Header title="MES - Dnevni izvještaj" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <div className="no-print">
            <MesDateSelector
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              showTimelineIndicators={true}
            />
          </div>

          {report && renderHeaderMeta(report)}

          {loading && !report && <div className="loading-state">Učitavanje izvještaja…</div>}

          {!loading && !report && (
            <div className="mes-section empty-state">
              <p>Nema izvještaja za {fmtDateHr(selectedDate)}</p>
              <span className="empty-hint">
                Odaberite datum s potvrđenim planom ili osvježite prikaz.
              </span>
            </div>
          )}

          {report && (
            <>
              <div className="kpi-tabs report-tabs no-print">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`kpi-tab ${activeTab === tab.key ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === "summary" && (
                <>
                  {renderKpiTable(report)}
                  {renderBanner(report)}
                  {renderTimeline()}
                  {renderComparison(report)}
                </>
              )}

              {activeTab === "plan" && (
                <>
                  {renderOperationalPlan(report)}
                  {renderFinancialPlan(report)}
                </>
              )}

              {activeTab === "actual" && renderRealized(report)}
            </>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default DailyReport;
